import { useEffect } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { ByzMuiDateTimePicker } from 'src/components/core-components/mui/inputs/ByzMuiDateTimePicker';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import { useTranslate } from "src/locales";
import { useAppSelector } from 'src/app/hooks';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';
import ByzMuiTextInputWithMic from 'src/components/core-components/mui/inputs/ByzMuiTextInputWithMic';

interface PatientMonitoringFormData {
  ID?: string;
  CUSER: string;
  CTIME: Date | null;
  PTIME: Date | null;
  NOTE: string;
  WRITE_ON_EPICRISE: string;
  PT_ID: string;
  NURSE_ID: string;
}

interface PatientMonitoringProps {
  recordId?: string;
  patientId?: string;
  onClose?: () => void;
  onSave?: () => void;
  onChange: (field: string, value: any) => void;
  formData: any;
}

function PatientMonitoringCreate({ recordId, onChange, formData }: PatientMonitoringProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!recordId && !formData.PTIME) {
      onChange("CUSER", user?.username ?? "");
      onChange("NURSE_ID", user?.id?.toString() ?? "");
      onChange("CTIME", new Date());
      onChange("PTIME", new Date());
      onChange("WRITE_ON_EPICRISE", formData.WRITE_ON_EPICRISE ?? "0");
    }
  }, [recordId, formData.PTIME, formData.WRITE_ON_EPICRISE, onChange, user?.id, user?.username]);

  const typedFormData = formData as PatientMonitoringFormData;

  return (
    <>
      <Box sx={{ padding: '20px', width: '100%' }}>
        <Grid container spacing={2}>
          <Grid md={6} xs={12} container direction={'column'} spacing={3}>
            <Grid md={12} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
              <ByzMuiTypography isTitle={true}>{t("patient-monitoring.title")}</ByzMuiTypography>
            </Grid>

            <Grid container alignItems={'center'} justifyContent={'space-between'}>
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                <ByzMuiTypography sx={{ marginLeft: '30px' }}>{t("processtime")}</ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiDateTimePicker
                  value={typedFormData.PTIME ? (typeof typedFormData.PTIME === 'string' ? new Date(typedFormData.PTIME) : typedFormData.PTIME) : null}
                  onChange={(value) => onChange("PTIME", value)}
                  sx={{ width: "100%" }}
                  label={t("processtime")}
                />
              </Grid>
            </Grid>

            <Grid container alignItems={'center'} justifyContent={'space-between'}>
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                <ByzMuiTypography sx={{ marginLeft: '30px' }}>{t("createdby")}</ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  value={typedFormData.CUSER ?? ""}
                  label={t("createdby")}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid md={12} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
              <ByzMuiTypography isTitle={true}>{t("patient-monitoring.note")}</ByzMuiTypography>
            </Grid>

            <Grid container alignItems={'stretch'} justifyContent={'space-between'}>
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', display: 'flex', alignItems: 'center' }}>
                <ByzMuiTypography sx={{ marginLeft: '30px' }}>{t("note")}</ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextInputWithMic
                  sx={{ width: "100%" }}
                  value={typedFormData.NOTE ?? ""}
                  onChange={(e: any) => onChange("NOTE", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
            <Grid container alignItems={'center'} justifyContent={'space-between'}>
              <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                <ByzMuiTypography sx={{ marginLeft: '30px' }}>{t("patient-monitoring.write-on-epicrise")}</ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiCheckBox
                  sx={{ width: "100%" }}
                  checked={(typedFormData.WRITE_ON_EPICRISE ?? "0") === "1"}
                  onChange={(e: any) => onChange("WRITE_ON_EPICRISE", e.target.checked ? "1" : "0")}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default PatientMonitoringCreate;
